import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, AlertCircle, DollarSign, Users, TrendingUp, X, RefreshCw, Archive, Undo2 } from "lucide-react";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

/**
 * RevshareImport — 3-step Everflow CSV import.
 *
 *   1. Choose CSV file (drag & drop or click).
 *   2. Backend previews the file: shows column mapping, per-affiliate summary, warnings.
 *   3. Admin reviews → clicks confirm → rows are inserted (idempotent by external_txn_id).
 */
export default function RevshareImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [sourceNote, setSourceNote] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchActionId, setBatchActionId] = useState(null);
  const inputRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/revshare/batches?limit=50");
      setBatches(data || []);
    } catch (_) {
      // non-fatal: show empty list
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const onSelectFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("רק קבצי CSV");
      return;
    }
    setFile(f);
    setPreview(null);
    setConfirmed(null);
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/revshare/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`נמצאו ${data.matched_count} התאמות`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה בקריאת הקובץ");
    } finally {
      setLoading(false);
    }
  };

  const doConfirm = async () => {
    if (!file || !preview) return;
    if (preview.matched_count === 0) {
      toast.error("אין שורות תואמות לייבוא");
      return;
    }
    if (!window.confirm(`לייבא ${preview.matched_count} שורות בסך $${preview.total_revshare}?`)) return;
    setConfirming(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_note", sourceNote);
      const { data } = await api.post("/admin/revshare/confirm", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConfirmed(data);
      toast.success(`יובאו ${data.inserted} שורות · $${data.total_revshare} revshare`);
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה בייבוא");
    } finally {
      setConfirming(false);
    }
  };

  const toggleBatchPaid = async (batch, nextPaid) => {
    const action = nextPaid ? "לסמן כשולם" : "להחזיר ללא-שולם";
    if (!window.confirm(`${action} את כל ${batch.count} השורות של ${batch._id}?`)) return;
    setBatchActionId(batch._id);
    try {
      const { data } = await api.post(`/admin/revshare/batches/${batch._id}/mark-paid?paid=${nextPaid}`);
      toast.success(`${data.updated} שורות עודכנו`);
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה");
    } finally {
      setBatchActionId(null);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setConfirmed(null);
    setSourceNote("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <AdminLayout title="ייבוא Revshare מ-Everflow">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Intro */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm leading-[1.7]">
          <b>איך זה עובד:</b> העלה CSV מ-Everflow (SubID report). המערכת מזהה עמודות אוטומטית
          (מחפשת <code className="bg-white/70 px-1 rounded ltr">sub_id_2</code> ל-lead_id ו-<code className="bg-white/70 px-1 rounded ltr">revenue</code>/<code className="bg-white/70 px-1 rounded ltr">payout</code>),
          מחשבת <b>revshare = revenue × affiliate.revshare_percent</b>, ומציגה תצוגה מקדימה לפני שמירה.
          Idempotent — שורות עם אותו <code className="bg-white/70 px-1 rounded ltr">conversion_id</code> לא ייובאו פעמיים.
        </div>

        {/* Step 1: file picker */}
        {!confirmed && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="revshare-uploader">
            <h2 className="font-[800] text-lg mb-3">1. בחר קובץ CSV</h2>
            {!file ? (
              <label
                htmlFor="revshare-file"
                className="block border-2 border-dashed border-stone-300 rounded-xl p-10 text-center cursor-pointer hover:border-brand-amber hover:bg-amber-50 transition"
              >
                <Upload className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                <div className="font-bold mb-1">גרור לכאן CSV או לחץ לבחירה</div>
                <div className="text-xs text-stone-500">Everflow SubID report (UTF-8 / CP1255 / Latin-1)</div>
                <input
                  ref={inputRef}
                  id="revshare-file"
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => onSelectFile(e.target.files?.[0])}
                  data-testid="revshare-file-input"
                />
              </label>
            ) : (
              <div className="flex items-center justify-between bg-stone-50 rounded-xl p-4 border border-stone-200" data-testid="revshare-file-selected">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-8 h-8 text-brand-amber flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-bold truncate">{file.name}</div>
                    <div className="text-xs text-stone-500">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!preview && (
                    <Button
                      onClick={doPreview}
                      disabled={loading}
                      data-testid="revshare-preview-btn"
                      className="bg-brand-charcoal text-white rounded-full h-10"
                    >
                      {loading ? <RefreshCw className="w-4 h-4 animate-spin me-2" /> : <TrendingUp className="w-4 h-4 me-2" />}
                      {loading ? "מנתח..." : "תצוגה מקדימה"}
                    </Button>
                  )}
                  <Button variant="outline" onClick={reset} className="rounded-full h-10 w-10 p-0" data-testid="revshare-reset">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: preview */}
        {preview && !confirmed && (
          <>
            {/* Column mapping */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="revshare-columns">
              <h2 className="font-[800] text-lg mb-3">2. עמודות שזוהו</h2>
              <div className="grid sm:grid-cols-5 gap-3">
                {["lead_id", "revenue", "payout", "txn_id", "timestamp"].map((k) => (
                  <div key={k} className={`rounded-xl p-3 border ${preview.columns[k] ? "bg-emerald-50 border-emerald-200" : "bg-stone-50 border-stone-200"}`} data-testid={`col-${k}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{k}</div>
                    <div className={`text-sm font-mono mt-1 ltr ${preview.columns[k] ? "text-emerald-700" : "text-stone-400"}`}>
                      {preview.columns[k] || "— לא נמצא"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* KPI totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="שורות בקובץ" value={preview.total_rows} testId="rev-total" accent="blue" icon={FileText} />
              <Kpi label="התאמות" value={preview.matched_count} testId="rev-matched" accent="emerald" icon={CheckCircle2} />
              <Kpi label="ללא ליד במערכת" value={preview.unmatched_count} testId="rev-unmatched" accent="orange" icon={AlertCircle} />
              <Kpi label="Revshare חדש" value={`$${preview.total_revshare}`} testId="rev-total-rs" accent="amber" icon={DollarSign} />
            </div>

            {/* Per-affiliate summary */}
            {preview.summary_by_affiliate?.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="revshare-aff-summary">
                <h3 className="font-[800] mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-brand-amber" />סיכום לפי אפיליאייט</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-right border-b border-stone-100">
                        <th className="py-2 font-bold text-xs uppercase text-stone-500">אפיליאייט</th>
                        <th className="py-2 font-bold text-xs uppercase text-stone-500">%</th>
                        <th className="py-2 font-bold text-xs uppercase text-stone-500">התאמות</th>
                        <th className="py-2 font-bold text-xs uppercase text-stone-500">Revenue</th>
                        <th className="py-2 font-bold text-xs uppercase text-stone-500">Revshare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.summary_by_affiliate.map((s) => (
                        <tr key={s.affiliate_id} className="border-b border-stone-50" data-testid={`aff-row-${s.affiliate_id}`}>
                          <td className="py-2">
                            <div className="font-bold">{s.affiliate_name}</div>
                            <code className="text-[10px] ltr bg-stone-100 rounded px-1">{s.list_id}</code>
                          </td>
                          <td className="py-2 ltr">{s.revshare_percent}%</td>
                          <td className="py-2 font-bold">{s.matches}</td>
                          <td className="py-2 ltr">${s.total_revenue}</td>
                          <td className="py-2 font-bold text-brand-amber ltr">${s.total_revshare}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Row preview */}
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <h3 className="font-[800] mb-3">שורות (עד 500)</h3>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs" data-testid="revshare-rows">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-right border-b border-stone-200">
                      <th className="py-2 font-bold text-stone-500">#</th>
                      <th className="py-2 font-bold text-stone-500">Lead ID</th>
                      <th className="py-2 font-bold text-stone-500">אפיליאייט</th>
                      <th className="py-2 font-bold text-stone-500">Revenue</th>
                      <th className="py-2 font-bold text-stone-500">Revshare</th>
                      <th className="py-2 font-bold text-stone-500">סטטוס</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.row_index} className={`border-b border-stone-50 ${r.matched ? "" : "opacity-60"}`}>
                        <td className="py-1.5 text-stone-400 ltr">{r.row_index}</td>
                        <td className="py-1.5 font-mono ltr text-[11px]">{r.lead_id || "—"}</td>
                        <td className="py-1.5">{r.affiliate_name || <span className="text-stone-400">—</span>}</td>
                        <td className="py-1.5 ltr">${r.revenue}</td>
                        <td className="py-1.5 font-bold text-brand-amber ltr">${r.computed_revshare}</td>
                        <td className="py-1.5">
                          {r.matched
                            ? <span className="text-emerald-600 text-xs">✓ תואם</span>
                            : <span className="text-orange-600 text-xs">⚠ {warningLabel(r.warning)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.row_limit_hit && (
                <div className="text-xs text-stone-500 mt-2">
                  ⚠ מוצגות 500 שורות ראשונות בלבד בתצוגה מקדימה. בהמשך יעברו כל השורות.
                </div>
              )}
            </div>

            {/* Confirm */}
            <div className="bg-brand-charcoal text-white rounded-2xl p-6" data-testid="revshare-confirm-panel">
              <h2 className="font-[800] text-lg mb-3">3. אישור וייבוא</h2>
              <div className="mb-4">
                <Label className="text-white/80 text-sm mb-1.5 block">הערה על היבוא (לאודיט)</Label>
                <Input
                  value={sourceNote}
                  onChange={(e) => setSourceNote(e.target.value)}
                  placeholder="לדוגמה: EF Report Aug 2026"
                  className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="revshare-note"
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-white/70">
                  יעודכנו {preview.matched_count} שורות · <b className="text-brand-amber">${preview.total_revshare}</b> revshare.
                  {preview.duplicate_count > 0 && <span className="text-orange-300"> ({preview.duplicate_count} כפולות יידלגו)</span>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reset} className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10">ביטול</Button>
                  <Button
                    onClick={doConfirm}
                    disabled={confirming || preview.matched_count === 0}
                    data-testid="revshare-confirm-btn"
                    className="bg-brand-amber hover:bg-brand-amberHover text-white rounded-full font-bold"
                  >
                    {confirming ? <RefreshCw className="w-4 h-4 animate-spin me-2" /> : <CheckCircle2 className="w-4 h-4 me-2" />}
                    {confirming ? "מייבא..." : "אשר וייבא"}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Success */}
        {confirmed && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="revshare-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
            <h2 className="font-[800] text-2xl mb-2">היבוא הושלם ✅</h2>
            <div className="text-sm text-stone-600 mb-4 leading-[1.7]">
              יובאו <b className="text-emerald-700">{confirmed.inserted}</b> שורות בסך <b className="text-brand-amber">${confirmed.total_revshare}</b>.
              {confirmed.skipped_duplicate > 0 && <><br/>{confirmed.skipped_duplicate} דילוגים על שורות שכבר קיימות.</>}
              {confirmed.skipped_no_lead > 0 && <><br/>{confirmed.skipped_no_lead} דילוגים ללא ליד במערכת.</>}
            </div>
            <code className="text-[11px] ltr bg-white/60 rounded px-2 py-1">batch: {confirmed.batch_id}</code>
            <div className="mt-5">
              <Button onClick={reset} className="rounded-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="revshare-new-btn">
                <Upload className="w-4 h-4 me-2" />ייבוא נוסף
              </Button>
            </div>
          </div>
        )}

        {/* Past batches — audit + Mark as Paid */}
        {batches.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200" data-testid="revshare-batches">
            <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-[800] text-lg flex items-center gap-2">
                  <Archive className="w-5 h-5 text-brand-amber" />ייבואים אחרונים
                </h2>
                <p className="text-xs text-stone-500 mt-0.5">אחרי ששילמת לאפיליאייט ב-Everflow / ידנית, סמן כאן את ה-batch כדי שהדשבורד שלו יציג זאת.</p>
              </div>
              <Button variant="outline" onClick={loadBatches} className="rounded-full h-9 text-xs" data-testid="batches-refresh">
                <RefreshCw className="w-3.5 h-3.5 me-1" />רענן
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-right bg-stone-50 border-b border-stone-100">
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">Batch</th>
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">מקור</th>
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">תאריך</th>
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">שורות</th>
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">סה״כ</th>
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">שולם</th>
                    <th className="px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-stone-500">סטטוס</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => {
                    const fullyPaid = b.paid_count === b.count && b.count > 0;
                    const partial = b.paid_count > 0 && !fullyPaid;
                    return (
                      <tr key={b._id} className="border-b border-stone-50 hover:bg-stone-50" data-testid={`batch-row-${b._id}`}>
                        <td className="px-4 py-2.5">
                          <code className="text-[10px] ltr bg-stone-100 rounded px-1.5 py-0.5">{b._id}</code>
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          {b.source_file || <span className="text-stone-400">—</span>}
                          {b.source_note && <div className="text-[10px] text-stone-500 mt-0.5">{b.source_note}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-stone-500">{formatDate(b.imported_at)}</td>
                        <td className="px-4 py-2.5 ltr font-bold">{b.count}</td>
                        <td className="px-4 py-2.5 ltr font-bold text-brand-amber">${b.total_revshare}</td>
                        <td className="px-4 py-2.5 ltr">
                          <span className="font-bold text-emerald-700">${b.paid_revshare}</span>
                          {b.unpaid_revshare > 0 && <span className="text-stone-400 text-xs"> / ${b.unpaid_revshare} ממתין</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          {fullyPaid
                            ? <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 text-[10px] font-bold">✓ שולם במלואו</span>
                            : partial
                              ? <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 text-[10px] font-bold">⟳ חלקי</span>
                              : <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-600 rounded-full px-2 py-0.5 text-[10px] font-bold">ממתין</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          {fullyPaid ? (
                            <Button
                              variant="outline"
                              onClick={() => toggleBatchPaid(b, false)}
                              disabled={batchActionId === b._id}
                              className="rounded-full h-8 text-[11px]"
                              data-testid={`batch-unpaid-${b._id}`}
                            >
                              {batchActionId === b._id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Undo2 className="w-3 h-3 me-1" />בטל</>}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => toggleBatchPaid(b, true)}
                              disabled={batchActionId === b._id}
                              className="rounded-full h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`batch-paid-${b._id}`}
                            >
                              {batchActionId === b._id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 me-1" />סמן כשולם</>}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Kpi({ label, value, testId, accent, icon: Icon }) {
  const colors = {
    amber: "bg-amber-50 border-amber-200 text-brand-amber",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    stone: "bg-stone-50 border-stone-200 text-stone-500",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[accent] || colors.stone}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      </div>
      <div className="text-3xl font-[800] mt-1">{value ?? 0}</div>
    </div>
  );
}

function warningLabel(w) {
  return ({
    empty_lead_id: "ללא lead_id",
    lead_not_found: "ליד לא נמצא",
    lead_without_affiliate: "ליד ללא אפיליאייט",
    duplicate_txn: "כבר יובא",
  })[w] || w;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("he-IL", {
      day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}
